import { PercentageSlider } from "components/PercentageSlider/PercentageSlider";
import { sum } from "lodash-es";
import { forwardRef, useCallback, useMemo, useRef } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { Token } from "store/global/TokenEnum";
import invariant from "tiny-invariant";

interface TradeLegMakerSliderProps {
  symbol: Token;
  color: string;
}

export const TradeLegMakerSlider = forwardRef<HTMLDivElement, TradeLegMakerSliderProps>(
  ({ symbol, color }, ref) => {
    const lastAdjusted = useRef(0);
    const makerTokens = GlobalStore.useState(s => s.makerTokens);
    const isLocked = makerTokens.find(entry => entry.token === symbol)?.isLocked;
    const tokenEntry = makerTokens.find(entry => entry.token === symbol);
    const currentSliderIndex = makerTokens.findIndex(entry => entry.token === symbol);

    const maxAllowedValue = useMemo(() => {
      const deductables = makerTokens.map(entry => {
        if (entry.isLocked && entry.token !== symbol) {
          return entry.pct;
        } else {
          return 1;
        }
      });
      const maxAllowedValue = 101 - deductables.reduce((partialSum, a) => partialSum + a, 0);
      return maxAllowedValue;
    }, [makerTokens, symbol]);

    const canUpdateTokensFurther = useCallback(
      (increment: number) => {
        const canIncrement = makerTokens.some((token, index) => {
          return index !== currentSliderIndex && token.pct - increment > 0 && !token.isLocked;
        });
        return canIncrement;
      },
      [currentSliderIndex, makerTokens]
    );

    /**
     * On every tick of the slider the function will calculate the delta and
     * redistribute among the rest in a round-robin fashion. The function also
     * keeps track of which slider was adjusted last so that consecutive
     * adjustments are distributed equally.
     */
    const setPercentageSliders = useCallback(
      (value: number) => {
        if (tokenEntry) {
          if (value > maxAllowedValue) {
            value = maxAllowedValue;
          } else if (value <= 0) {
            value = 1;
          }
          let remaining = value - tokenEntry.pct; // calculate delta
          const makerTokensPcts = makerTokens.map(entry => entry.pct);
          makerTokensPcts[currentSliderIndex] = value;

          // Redistribute values in round-robin fashion
          while (remaining !== 0) {
            const currIdx = lastAdjusted.current % makerTokens.length;
            lastAdjusted.current++;
            const increment = remaining > 0 ? 1 : -1;
            if (!canUpdateTokensFurther(increment)) break;
            if (currIdx === currentSliderIndex) continue;
            if (makerTokensPcts[currIdx] - increment <= 0) continue;
            if (makerTokens[currIdx].isLocked) continue;
            makerTokensPcts[currIdx] -= increment;
            remaining -= increment;
          }
          invariant(
            sum(makerTokensPcts) === 100,
            "Sum of maker token percentages does not equal 100."
          );
          GlobalStore.update(s => {
            s.makerTokens = s.makerTokens.map((entry, i) => {
              entry.pct = makerTokensPcts[i];
              return entry;
            });
          });
        }
      },
      [tokenEntry, maxAllowedValue, makerTokens, currentSliderIndex, canUpdateTokensFurther]
    );

    return (
      <PercentageSlider
        ref={ref as React.Ref<HTMLDivElement>}
        value={tokenEntry?.pct ?? 1}
        setValue={setPercentageSliders}
        color={color}
        minValue={1}
        maxValue={maxAllowedValue}
        isLocked={isLocked ?? false}
        data-testid={`${symbol}-slider`}
      />
    );
  }
);
